import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ChartType } from 'angular-google-charts';
import { AttacheService } from 'src/app/services/attache/attache.service';
import { CritereService } from 'src/app/services/critere/critere.service';
import { LoginService } from 'src/app/services/login/login.service';
import { MaquetteService } from 'src/app/services/maquette/maquette.service';
import { PatientService } from 'src/app/services/patient/patient.service';
import { Critere, FormatEnum, TypeEnum } from 'src/app/utils/interfaces/new/Critere';
import { CritereAttache } from 'src/app/utils/interfaces/new/CritereAttache';
import { InfosPatient } from 'src/app/utils/interfaces/new/infos-patient.interface';
import { Maquette } from 'src/app/utils/interfaces/new/Maquette';
import { MaquetteGuide } from 'src/app/utils/interfaces/new/maquette-guide.interface';
import { Profil } from 'src/app/utils/interfaces/new/profil.interface';
import { Diagnostic } from 'src/app/utils/interfaces/new/diagnostic.interface';

@Component({
  selector: 'app-calcul-imc',
  templateUrl: './calcul-imc.component.html',
  styleUrls: ['./calcul-imc.component.scss', '../modal.fragment.scss']
})
export class CalculImcComponent implements OnInit {
  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;
  @Input() patientId!: string;
  @Input() profil!: Profil;
  @Input() currentDiag!: Diagnostic;
  @Output() public closeEmitter = new EventEmitter();
  @Output() maquetteAdded = new EventEmitter<{ idMaquette: string, attaches: CritereAttache[], valeurs?: Object , criteresSansAttaches?: Critere[]}>();


  public formGroup: FormGroup;
  public imcValue: number | null = null;
  public isImcResult: boolean = false;
  public errorMessage: string = '';
  public isError: boolean = false;
  public isSubmitDisabled: boolean = false;
  public infosPatient: InfosPatient;
  public currentDiagnosticGuide: MaquetteGuide[];
  public poids: number;
  public taille: number;
  public imc: number;

  private readonly defaultColor = "#d3d3d3";
  private readonly underweightColor = "#73caf7";
  private readonly normalWeightColor = "#b1ca39";
  private readonly overweightColor = "#fdb801";
  private readonly obesityColor = "#f69103";
  private readonly severeObesityColor = "#ed5b41";


  public chart = {
    title: "Classification IMC",
    type: ChartType.PieChart,
    data: [
      ["Maigreur (< 18.5)", 10],
      ["Normal (18.5 - 24.9)", 10],
      ["Surpoids (25 - 29.9)", 10],
      ["Obésité (30 - 39.9)", 10],
      ["Obésité sévère (> 40)", 10],
      ["", 50] // Partie cachée pour avoir un demi donut
    ],
    options: {
      title: "Diagramme IMC",
      pieHole: 0.6,
      pieStartAngle: -90, // Demi-donut
      slices: {
        5: { offset: 0.3, color: "transparent" }
      },
      chartArea: { width: "100%", height: "100%" },
      legend: { position: 'right' },
      pieSliceText: "none",
      annotations: {
        textStyle: {
          fontSize: 16,
          bold: true
        }
      },
      colors: [this.underweightColor, this.normalWeightColor, this.overweightColor,this.obesityColor, this.severeObesityColor] // Gris par défaut
    },
  };

  constructor(
    private _formBuilder: FormBuilder,
    private _patient: PatientService,
    private _login: LoginService,
    private _maquette: MaquetteService,
    private _attache: AttacheService,
    private _critere: CritereService,

  ) { }

  async ngOnInit(): Promise<void> {
    this.infosPatient = await this._patient.findOnePatientInfos(this.patientId);

    this._initForm();
  }

  private _initForm() {
    const patientGender = this.infosPatient?.sexe || '';
    const patientWeight = this.infosPatient?.poids || '';
    const patientHeight = this.infosPatient?.taille || '';

    this.formGroup = this._formBuilder.group({
      isFemme: [patientGender === 'FEMME'],
      isHomme: [patientGender === 'HOMME'],
      poids: [patientWeight, [Validators.required, Validators.min(0.4)]],
      taille: [patientHeight, [Validators.required, Validators.max(250), Validators.min(20)]],
    }, { validator: this.selectAGender });

    // On s'assure que l'utilisateur ne puisse pas sélectionner les deux genres en même temps
    this.formGroup.get('isFemme')?.valueChanges.subscribe(value => {
      if (value) {
        this.formGroup.get('isHomme')?.setValue(false, { emitEvent: false });
      }
      this.formGroup.updateValueAndValidity();
    });

    this.formGroup.get('isHomme')?.valueChanges.subscribe(value => {
      if (value) {
        this.formGroup.get('isFemme')?.setValue(false, { emitEvent: false });
      }
      this.formGroup.updateValueAndValidity();
    });
  }


  /**
   * Vérifie qu'au moins une des cases (homme ou femme) est cochée
   */
  private selectAGender(formGroup: FormGroup) {
    const isFemme = formGroup.get('isFemme')?.value;
    const isHomme = formGroup.get('isHomme')?.value;

    return isFemme || isHomme ? null : { genderRequired: true };
  }


  public calculateImc() {
    const focusMissingField = () => {
      /**
       * Cette fonction parcour les champ required du formGroup
       * et donne le focus au premier champ non valide trouvé
       */
      const requiredFields = ['isFemme', 'isHomme', 'taille', 'poids'];
      for (const field of requiredFields) {
        if (!this.formGroup?.controls?.[field]?.valid) {
          const f = document.querySelector(`app-calcul-imc #${field} input`);
          if (f) {
            (f as any).focus();
            break;
          }
        }
      }
    };

    if (!this.formGroup.valid) {
      this.errorMessage = 'Un champ obligatoire est manquant';
      this.isError = true;
      focusMissingField();
      this.isSubmitDisabled = true;
      this.formGroup.markAllAsTouched();
      return;
    } else {
      this.errorMessage = undefined;
      this.isError = false;
    }

    if (this.formGroup.valid) {
    const poidsRaw = this.formGroup.get('poids')?.value?.toString().replace(',', '.');
    const tailleCmRaw = this.formGroup.get('taille')?.value?.toString().replace(',', '.');

    const poidsParsed = poidsRaw ? parseFloat(poidsRaw) : NaN;
    const tailleParsed = tailleCmRaw ? parseFloat(tailleCmRaw) / 100 : NaN;
   

      if (!isNaN(poidsParsed) && !isNaN(tailleParsed)) {
        this.poids = poidsParsed;
        this.taille = tailleParsed;

        const imcCalculated = this.poids / (this.taille * this.taille);
        this.imc = parseFloat(imcCalculated.toFixed(1));
        this.updateChart(this.imc);
        this.isImcResult = true;
      } else {
        this.isImcResult = false;
        this.errorMessage = 'Valeur de poids ou de taille invalide';
        this.isError = true;
      }
    }
  }

  private updateChart(imc: number) {
    const femme = this.formGroup.get('isFemme')?.value;
    const homme = this.formGroup.get('isHomme')?.value;

    if (!femme && !homme) {
      console.warn("Sélectionner un genre avant de calculer l'IMC.");
      return;
    }
   

    let colors = [this.defaultColor, this.defaultColor, this.defaultColor, this.defaultColor, this.defaultColor];

    if (femme) {
      if (imc < 18.5) {
        colors[0] = this.underweightColor;
      } else if (imc >= 18.5 && imc < 24) {
        colors[1] = this.normalWeightColor;
      } else if (imc >= 24 && imc < 29) {
        colors[2] = this.overweightColor;
      } else if (imc >= 29 && imc < 39) {
        colors[3] = this.obesityColor;
      } else {
        colors[4] = this.severeObesityColor;
      }
    } else if (homme) {
      if (imc < 18.5) {
        colors[0] = this.underweightColor;
      } else if (imc >= 18.5 && imc < 25) {
        colors[1] = this.normalWeightColor;
      } else if (imc >= 25 && imc < 30) {
        colors[2] = this.overweightColor;
      } else if (imc >= 30 && imc < 40) {
        colors[3] = this.obesityColor;
      } else {
        colors[4] = this.severeObesityColor;
      }
    }

    this.chart = {
      ...this.chart,
      options: {
        ...this.chart.options,
        colors: colors,
        pieSliceText: "none",
        annotations: {
          textStyle: {
            fontSize: 18,
            bold: true,
          }
        }
      }
    };
  }

  /**
   * 
   * @returns les critères "Taille", "Poids" et "IMC" s'ils existent, sinon les crée
   */
  public async searchExistingCritere(): Promise<[Critere, Critere, Critere]> {
    const criteresNoms = ["Taille", "Poids", "IMC"];// La recherche dans la bdd est insensible à la casse

    const fetchOrCreateCritere = async (name: string): Promise<Critere> => {
      let critere = await this._critere.findOneByName(name);
      if (!critere) {
        const newCritere: Critere = {
          author: this.profil.id,
          name: name,
          type: TypeEnum.Numerique,
          format:FormatEnum.FormatMillieme
        };
        const critereId = await this._critere.create(newCritere);
        critere = { ...newCritere, id: critereId };
      }
      return critere;
    };

    const [tailleCritere, poidsCritere, imcCritere] = await Promise.all(
      criteresNoms.map(fetchOrCreateCritere)
    );

    return [tailleCritere, poidsCritere, imcCritere];
  }

  /**
   * 
   * @returns la maquette IMC si elle existe, sinon la crée
   */
  public async generateMaquette() {
    const maquettes = await this._maquette.find();
    const maquetteIMC = maquettes.objects.find(maquette => maquette.name.toLocaleLowerCase() === "imc")
    // Cas où il n'y a pas de maquette "imc": 
    // On crée une maquette "IMC" -> on recherche les critères "taille" "poids" et "imc", s'ils existent on les "attache" à la maquette IMC.
    if (!maquetteIMC) {
      const newMaquette: Maquette = {
        author: this._login.getCurrentProfil().id,
        name: "IMC",
      }
      const idMaquette = await this._maquette.create(newMaquette);
      const [tailleCritere, poidsCritere, imcCritere] = await this.searchExistingCritere()

      const attaches: CritereAttache[] = [
        { critereId: tailleCritere.id, maquetteId: idMaquette, order: 1 },
        { critereId: poidsCritere.id, maquetteId: idMaquette, order: 2 },
        { critereId: imcCritere.id, maquetteId: idMaquette, order: 3 },
      ];

      await this._attache.create(attaches);

      return { attaches, idMaquette };
    } else {
      //Cas où on a déjà une maquette imc:
      // -> On regarde si elle a des attaches:
      const attachesMaquette = await this._attache.findByMaquetteId(maquetteIMC.id)
      const attaches = attachesMaquette.objects;

      // -> Si pas d'attaches, on recherche si les critères "taille", "poids" et "imc" existent 
      // s'ils existent on va les attacher à cette maquette.
      if (!attaches || attaches.length === 0) {
        const [tailleCritere, poidsCritere, imcCritere] = await this.searchExistingCritere()
        const criteresSansAttaches: Critere[] = [
          { id: tailleCritere.id, author: this.profil.id, name: tailleCritere.name, type: TypeEnum.Alphanumerique },
          { id: poidsCritere.id, author: this.profil.id, name: poidsCritere.name, type: TypeEnum.Alphanumerique },
          { id: imcCritere.id, author: this.profil.id, name: imcCritere.name, type: TypeEnum.Alphanumerique }
        ]

        return { idMaquette: maquetteIMC.id, attaches: undefined, criteresSansAttaches };

      } else {
        //-> si la maquette a des attaches:
        // On vérifie qu'elle ait bien tous les critères, sinon on identifie le critère manquant et on crée son attache 

        const [tailleCritere, poidsCritere, imcCritere] = await this.searchExistingCritere();

        const attachesExistantes = attaches.map(attache => attache.critereId);

        const criteresManquants = [tailleCritere, poidsCritere, imcCritere].filter(
          critere => !attachesExistantes.includes(critere.id)
        );

        // Création des attaches pour les critères manquants
        if (criteresManquants.length > 0) {
          const nouvellesAttaches: CritereAttache[] = criteresManquants.map((critere, index) => ({
            critereId: critere.id,
            maquetteId: maquetteIMC.id,
            order: attaches.length + index + 1
          }));

          await this._attache.create(nouvellesAttaches);
        }

        const attachesMisesAJour = await this._attache.findByMaquetteId(maquetteIMC.id);

        return { idMaquette: maquetteIMC.id, attaches: attachesMisesAJour };
      }
      
    }

  }

  /**
   * Ajoute la maquette IMC à la consultation en cours
   */
  public async addMaquetteToCurrentConsultation() {
    const maquetteInfos = await this.generateMaquette();

    const valeurs = {
      poids: this.poids,
      taille: this.taille,
      imc: this.imc
    };

    this.maquetteAdded.emit({
      idMaquette: maquetteInfos.idMaquette,
      attaches: maquetteInfos.attaches || undefined,
      valeurs: valeurs, 
      criteresSansAttaches:maquetteInfos.criteresSansAttaches || undefined
    });

    this.closeModal();
  }


  public closeModal(): void {
    this.closeEmitter.emit();
  }
}
